import { useQuery } from '@tanstack/react-query'

import { NotificationsAPI, TeamsAPI, ToolbarAPI, TrashAPI } from '@makyoapp/makyo-sdk'
import { Order } from '@makyoapp/makyo-sdk/api/api'
import { NotificationOrderBy } from '@makyoapp/makyo-sdk/models/Notifications'
import { TeamOrderBy } from '@makyoapp/makyo-sdk/models/Teams'
import { fromSdkAppList, fromSdkTeamList, fromSdkTrashList } from '@makyoapp/makyo-common'
import { projectPlanColor } from '@makyoapp/makyo-ui'

import { queryKeys } from '@/lib/react-query'
import { useMyNotificationsContext } from '@/context/MyNotificationsContext'
import useAuth from '@/hooks/useAuth'
import { useDeferRequests } from '@/hooks/useDeferRequests'
import { useReqError } from '@/hooks/useReqError'

/**
 * Get user's recent apps
 *
 * @info
 */
export const useGetRecentApps = () => {
  const { userData } = useAuth()
  const userId = userData?.id
  const { shouldFetch } = useDeferRequests()

  return useQuery({
    queryKey: [queryKeys.recentApps, userId],
    queryFn: () => {
      return ToolbarAPI.ReqGet().then((res) => fromSdkAppList(res, projectPlanColor))
    },
    enabled: !!userId && shouldFetch,
  })
}

/**
 * Get team list query
 *
 * @info use manual filter if we want to show only teams that user is part of
 */
export const useGetMyTeams = () => {
  return useQuery({
    queryKey: [queryKeys.user.teams],
    queryFn: () => TeamsAPI.ReqList(undefined, Order.Desc, TeamOrderBy.CreatedAt, undefined).then(fromSdkTeamList),
  })
}

/**
 * A custom React Hook for fetching a list of notifications.
 *
 * @param {object} opts - The options for the query.
 * @param {boolean} [opts.enabled] - Whether the query is enabled or not. Default is `false`.
 * @param {number} opts.page - The page number for the query.
 * @returns {object} The query object containing `isLoading`, `error`, and `data` properties.
 *
 * @example
 * const { isLoading, error, data } = useNotificationListQuery({ enabled: true, page: 1 });
 */
export const useNotificationListQuery = (opts: { enabled?: boolean; page: number }) => {
  const { setIsLoading } = useMyNotificationsContext()
  const printAPIError = useReqError()

  setIsLoading(true)
  return useQuery({
    queryKey: [queryKeys.user.notificationList, opts.page],
    queryFn: () =>
      NotificationsAPI.ReqList(opts.page, Order.Desc, NotificationOrderBy.CreatedAt)
        .then((res) => res)
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => {
          setIsLoading(false)
        }),
    enabled: opts?.enabled,
  })
}

/**
 * Get user's trash list
 *
 * @info
 */
export const useGetMyTrashList = () => {
  return useQuery({
    queryKey: [queryKeys.user.trash],
    queryFn: () => TrashAPI.ReqList(),
  })
}
